import { useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { animeApi } from "../api/anime";
import styles from "./AnimeCard.module.css";

interface AnimeCardProps {
  url: string;
  title: string;
  imageUrl: string;
  subtitle?: string;
  /** 0..1: barra de progreso para "continuar viendo". */
  progress?: number;
  to?: string;
  /** Marca la imagen como candidata LCP (fetchpriority="high", sin lazy loading). */
  priority?: boolean;
  /** Etiqueta flotante superior izquierda (p. ej. tipo, proveedor o posicion). */
  badge?: string;
  /** Puntuacion opcional o nota personal (esquina superior derecha). */
  score?: string | number | null;
  /** Indice para escalonar sutilmente la animacion de entrada. */
  index?: number;
}

const PREFETCH_DELAY_MS = 120;

export function AnimeCard({ url, title, imageUrl, subtitle, progress, to, priority = false, badge, score, index = 0 }: AnimeCardProps) {
  const href = to ?? `/anime?url=${encodeURIComponent(url)}`;
  const queryClient = useQueryClient();
  const hoverTimer = useRef<number | null>(null);

  const schedulePrefetch = () => {
    if (!url) return;
    if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => {
      void queryClient.prefetchQuery({
        queryKey: ["info", url],
        queryFn: () => animeApi.info(url),
        staleTime: 5 * 60_000,
      });
    }, PREFETCH_DELAY_MS);
  };

  const cancelPrefetch = () => {
    if (hoverTimer.current !== null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const stagger = Math.min(index, 12);

  return (
    <Link
      to={href}
      className={styles.card}
      style={{ "--stagger": stagger } as React.CSSProperties}
      onPointerEnter={schedulePrefetch}
      onPointerLeave={cancelPrefetch}
      onFocus={schedulePrefetch}
      onBlur={cancelPrefetch}
    >
      <div className={styles.cover}>
        {imageUrl ? (
          priority ? (
            <img
              src={animeApi.imageUrl(imageUrl)}
              alt=""
              width={240}
              height={360}
              decoding="async"
              {...({ fetchpriority: "high" } as Record<string, string>)}
            />
          ) : (
            <img src={animeApi.imageUrl(imageUrl)} alt="" width={240} height={360} loading="lazy" decoding="async" />
          )
        ) : (
          <div className={styles.placeholder} aria-hidden="true">
            <span>{title.slice(0, 1).toUpperCase()}</span>
          </div>
        )}
        {badge && <span className={styles.badge}>{badge}</span>}
        {score !== undefined && score !== null && score !== "" && <span className={styles.score}>★ {score}</span>}
        {progress !== undefined && (
          <div className={styles.progress} aria-label={`Visto ${Math.round(progress * 100)}%`}>
            <span style={{ width: `${Math.min(100, Math.max(0, progress * 100))}%` }} />
          </div>
        )}
      </div>
      <div className={styles.body}>
        <h3 className={styles.title}>{title}</h3>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
    </Link>
  );
}
